import Supercluster from 'common/utils/Cluster/Cluster';
import { MarkerType, ClusterZoom, OnClusteredData } from '../types';

interface DataPoint {
  geometry: {
    coordinates: [ number, number, number ]
  },
  data: any
};

export interface IconsCache {
  [key: string]: H.map.Icon;
}

type GetNoisePresentation = (props: {
  data: any;
  position: { lng: number; lat: number; alt: number; };
  iconsCache: IconsCache;
}) => H.map.Object | undefined;

type GetClusterPresentation = (props: {
  clusterWeight: number;
  position: { lng: number; lat: number; alt: number; };
  clusterId: number;
  iconsCache: IconsCache;
  clusterExpansionZoom: number;
}) => H.map.Object | undefined;

interface Options {
  type?: MarkerType;
  clusterZoom?: ClusterZoom;
}

interface LayerOptions {
  isUpdateLayer?: boolean;
}

export interface  HereMarkersProvider {
  getNoisePresentation: GetNoisePresentation;
  getClusterPresentation: GetClusterPresentation;
  updateDataPoints: (dataPoints?: DataPoint[], options?: Options & LayerOptions) => void;
  onClusteredData?: OnClusteredData;
}

export class MarkersProvider {
  // @ts-ignore
  private _superclaster: Supercluster;
  private _map: Here;
  private _iconsCache: IconsCache = {};
  private _markersGroup: any;
  private _prevZoom: number;
  private _mapviewchangeHandler: () => void = () => {};
  private _mapviewchangeendHandler: () => void = () => {};
  onClusteredData?: OnClusteredData;
  getNoisePresentation: GetNoisePresentation;
  getClusterPresentation: GetClusterPresentation;

  constructor({
    data = [],
    map,
    getNoisePresentation,
    getClusterPresentation,
    type,
    clusterZoom,
    onClusteredData
  }: {
    data?: DataPoint[];
    map: Here;
    getNoisePresentation: GetNoisePresentation;
    getClusterPresentation: GetClusterPresentation;
    type?: MarkerType;
    clusterZoom?: ClusterZoom;
    onClusteredData?: OnClusteredData;
  }) {
    this._map = map;
    this._prevZoom = this._getZoom();
    this.onClusteredData = onClusteredData;
    this.getNoisePresentation = getNoisePresentation;
    this.getClusterPresentation = getClusterPresentation;
    this.updateDataPoints(data, { type, clusterZoom });
    this._initMapViewChangeHandler();
    this._initMapViewChangeListener();
    this._initMapViewChangeEndHandler();
    this._initMapViewChangeEndListener();
  }

  updateDataPoints(dataPoints: DataPoint[] = [], props?: Options & LayerOptions) {
    const { isUpdateLayer = true, ...options } = props || {};

    if (isUpdateLayer) {
      this._initSuperclaster(options);
      // @ts-ignore
      this._superclaster.load(dataPoints);
    }

    this._updateIconsOnMap();

    if (this.onClusteredData) {
      const markerTrees = this._getMarkerTrees();
      this.onClusteredData(markerTrees);
    }
  }

  _getMarkerTrees() {
    return new Array(18).fill(0).reduce((acc,_, index) => {
      const zoomPoints = this._superclaster.getClusters([-180, -90, 180, 90], index);

      acc[index] = zoomPoints.map((point) => {
        if (point.id) {
          return {
            clusterId: point.id,
            coordinates: {
              longitude: point.geometry.coordinates[0],
              latitude: point.geometry.coordinates[0]
            },
            children: this._superclaster.getLeaves(point.id as number, 999999)
          }
        }
        return point;
      });
      return acc;
    }, {} as { [key: number]: any[] });
  }

  _initSuperclaster(options?: Options) {
    const {
      type = 'cluster',
      clusterZoom = {}
    } = options || {};

    this._superclaster = new Supercluster({
      radius: 200,
      minZoom: clusterZoom?.min ?? 0,
      maxZoom: clusterZoom?.max ?? 16,
      minPoints: type === 'cluster' ? 5 : 100000
    });
  }

  _updateIconsOnMap() {
    this.deleteGroup();
    const items = this._getItemsByZoom();
    const icons = this._getIcons(items);
    this._drawIcons(icons);
  }

  _getViewModelBoundingBox() {
    const boundingBox = this._map.getViewModel().getLookAtData().bounds.getBoundingBox();
    return {
      left: boundingBox.getLeft() - 2.5,
      top: boundingBox.getTop() + 2.5,
      right: boundingBox.getRight() + 2.5,
      bottom: boundingBox.getBottom() - 2.5,
    } as const;
  }

  _getIcons(clusterItem: any[]) {
    const icons = clusterItem.reduce((acc, item: any) => {
      let icon: H.map.Object | undefined;
      // is cluster
      if (item.properties) {
        const {
          properties: { point_count: clusterWeight, cluster_id: clusterId },
          geometry: { coordinates: [lng, lat] }
        } = item;
        icon = this.getClusterPresentation({
          clusterWeight,
          clusterId,
          position: { lat, lng, alt: 0 },
          iconsCache: this._iconsCache,
          clusterExpansionZoom: this._superclaster.getClusterExpansionZoom(clusterId)
        });
      } else {
        const { data, geometry: { coordinates: [lng, lat, alt] } } = item;
        icon = this.getNoisePresentation({
          data,
          position: { lng, lat, alt },
          iconsCache: this._iconsCache
        });
      }

      if (icon) {
        acc.push(icon);
      }

      return acc;
    }, [] as H.map.Object[]);

    return icons;
  }

  _getItemsByZoom() {
    const zoom = this._getZoom();

    if (zoom < 8) {
      return this._superclaster.getClusters([-180, -90, 180, 90], zoom);
    } else {
      const { left, bottom, right, top } = this._getViewModelBoundingBox();
      return this._superclaster.getClusters([left, bottom, right, top], zoom);
    }
  }

  _getZoom() {
    return Math.round(this._map.getZoom());
  }

  deleteGroup() {
    if (this._markersGroup) {
      this._map.removeObject(this._markersGroup);
      this._markersGroup = null;
    }
  }

  resetProvider() {
    this.deleteGroup();
    this._deleteMapViewChangeListener();
    this._deleteMapViewChangeEndListener();
  }

  _drawIcons(icons: any[]) {
    this._markersGroup = new H.map.Group({ objects: icons });
    this._map.addObject(this._markersGroup);
  }

  _initMapViewChangeHandler() {
    this._mapviewchangeHandler = () => {
      const zoom = this._getZoom();

      if (this._prevZoom !== zoom) {
        this.deleteGroup();
        this._prevZoom = zoom;
      }
    };
  }

  _initMapViewChangeListener() {
    this._map.addEventListener('mapviewchange', this._mapviewchangeHandler);
  }

  _deleteMapViewChangeListener() {
    this._map.removeEventListener('mapviewchange', this._mapviewchangeHandler);
  }

  _initMapViewChangeEndHandler() {
    this._mapviewchangeendHandler = () => {
      this._updateIconsOnMap();
    };
  }

  _initMapViewChangeEndListener() {
    this._map.addEventListener('mapviewchangeend', this._mapviewchangeendHandler);
  }

  _deleteMapViewChangeEndListener() {
    this._map.removeEventListener('mapviewchangeend', this._mapviewchangeendHandler);
  }
}

